import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { ChevronDown, Dumbbell, Calendar, Trash2, Share2 } from 'lucide-react-native';

interface WorkoutExercise {
  exercise_id: string;
  name: string;
  sets: {
    reps: number;
    weight: number;
    is_pr: boolean;
    media_url?: string | null;
  }[];
  notes?: string | null;
  is_competition?: boolean;
}

interface WorkoutPayload {
  exercises: WorkoutExercise[];
  units?: string;
}

interface Workout {
  id: string;
  user_id: string;
  gym_id: string | null;
  performed_at: string;
  payload: WorkoutPayload;
  created_at: string;
}

interface WorkoutLogCardProps {
  workout: Workout;
  isExpanded: boolean;
  theme: any;
  userUnits: string;
  displayWeight: (weight: number, fromUnits: string) => number;
  formatDate: (dateString: string) => string;
  onToggle: () => void;
  onDelete: () => void;
  onDeleteExercise: (exerciseIndex: number) => void;
  onShare: () => void;
}

export default function WorkoutLogCard({
  workout,
  isExpanded,
  theme,
  userUnits,
  displayWeight,
  formatDate,
  onToggle,
  onDelete,
  onDeleteExercise,
  onShare,
}: WorkoutLogCardProps) {
  const exerciseCount = workout.payload.exercises?.length || 0;
  const workoutPayload = typeof workout.payload === 'string' ? JSON.parse(workout.payload) : workout.payload;
  const workoutUnits = workoutPayload?.units || 'lbs';

  let totalVolume = 0;
  workout.payload.exercises?.forEach((exercise) => {
    exercise.sets.forEach((set) => {
      const convertedWeight = displayWeight(set.weight, workoutUnits);
      totalVolume += convertedWeight * set.reps;
    });
  });

  return (
    <TouchableOpacity
      style={[styles.sessionCard, {
        backgroundColor: theme.colors.surface,
        borderColor: theme.colors.border,
      }]}
      onPress={onToggle}>
      <View style={styles.sessionHeader}>
        <View style={styles.sessionHeaderLeft}>
          <Calendar size={20} color={theme.colors.accent} />
          <View>
            <Text style={[styles.sessionDate, { color: theme.colors.text }]}>
              {formatDate(workout.performed_at)}
            </Text>
          </View>
        </View>
        <View style={styles.sessionStats}>
          <Text style={[styles.statText, { color: theme.colors.textSecondary }]}>
            {exerciseCount} exercises · {totalVolume.toLocaleString()} {userUnits}
          </Text>
          <TouchableOpacity
            onPress={onShare}
            style={styles.deleteButton}
            accessibilityLabel="Share workout"
            accessibilityRole="button">
            <Share2 size={18} color={theme.colors.accent} />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={onDelete}
            style={styles.deleteButton}
            accessibilityLabel="Delete workout"
            accessibilityRole="button">
            <Trash2 size={18} color={theme.colors.textTertiary} />
          </TouchableOpacity>
          <ChevronDown
            size={20}
            color={theme.colors.textTertiary}
            style={{ transform: [{ rotate: isExpanded ? '180deg' : '0deg' }] }}
          />
        </View>
      </View>

      {isExpanded && (
        <View style={styles.sessionDetails}>
          {workout.payload.exercises?.map((exercise, idx) => (
            <View
              key={`${workout.id}-${exercise.exercise_id}-${idx}`}
              style={[styles.liftRow, { borderTopColor: theme.colors.border }]}>
              <View style={styles.liftInfo}>
                <Dumbbell size={16} color={theme.colors.accent} />
                <View style={{ flex: 1 }}>
                  <Text style={[styles.exerciseName, { color: theme.colors.text }]}>
                    {exercise.name}
                  </Text>
                  {exercise.notes && (
                    <Text style={[styles.notes, { color: theme.colors.textTertiary }]}>
                      {exercise.notes}
                    </Text>
                  )}
                  {exercise.sets.map((set, setIdx) => {
                    const convertedWeight = displayWeight(set.weight, workoutUnits);
                    return (
                      <View key={setIdx} style={styles.setRow}>
                        <Text style={[styles.liftStats, { color: theme.colors.textSecondary }]}>
                          Set {setIdx + 1}: {convertedWeight}{userUnits} × {set.reps}
                        </Text>
                        {set.is_pr && (
                          <View style={[styles.prBadge, { backgroundColor: theme.colors.warning + '20' }]}>
                            <Text style={[styles.prBadgeText, { color: theme.colors.warning }]}>PR</Text>
                          </View>
                        )}
                      </View>
                    );
                  })}
                </View>
              </View>
              <TouchableOpacity
                onPress={() => onDeleteExercise(idx)}
                style={styles.deleteExerciseButton}
                accessibilityLabel="Delete exercise"
                accessibilityRole="button">
                <Trash2 size={16} color={theme.colors.textTertiary} />
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  sessionCard: {
    borderRadius: 2,
    borderWidth: 1,
    marginBottom: 12,
    overflow: 'hidden',
  },
  sessionHeader: {
    padding: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 6,
  },
  sessionHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  sessionDate: {
    fontSize: 15,
    fontWeight: '700',
    fontFamily: 'Oswald_700Bold',
  },
  sessionStats: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 1,
    minWidth: 0,
  },
  statText: {
    fontSize: 12,
    flexShrink: 1,
    fontFamily: 'Inter_500Medium',
  },
  sessionDetails: {
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  liftRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderTopWidth: 1,
    gap: 8,
  },
  liftInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  deleteButton: {
    padding: 4,
  },
  deleteExerciseButton: {
    padding: 4,
    alignSelf: 'flex-start',
  },
  exerciseName: {
    fontSize: 15,
    fontWeight: '600',
  },
  liftStats: {
    fontSize: 14,
    fontWeight: '600',
  },
  prBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 2,
  },
  prBadgeText: {
    fontSize: 11,
    fontWeight: '700',
  },
  notes: {
    fontSize: 12,
    marginTop: 4,
    fontStyle: 'italic',
  },
  setRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
  },
});
